import * as React from 'react'
import { isAxiosError } from 'axios'
import {
  CheckCircle2,
  ChevronDown,
  Clock,
  Mail,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  useDealLeadCounts,
  useIntakeActivity,
  useIntakeIssues,
  useIntakePollStatus,
  usePollIntakeNow,
  useRetryIntakeMessage,
} from '@/hooks/use-deal-leads'
import { showError, showInfo, showSuccess } from '@/lib/toast'
import {
  relativeTime,
  summarizeIntake,
} from '@/features/deal-review/intake-summary'
import { PANEL_ACTION, PILL_TONE } from '@/features/deal-review/review-tones'
import type { DealIntakeActivityRow } from '@/types/api'

const ACTIVITY_TONE: Record<
  DealIntakeActivityRow['kind'],
  { icon: LucideIcon; className: string }
> = {
  received: { icon: Mail, className: 'text-muted-foreground' },
  ready: { icon: CheckCircle2, className: 'text-emerald-700' },
  failed: { icon: TriangleAlert, className: 'text-amber-700' },
}

function hasErrorCode(data: unknown): data is { errorCode: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'errorCode' in data &&
    typeof (data as Record<string, unknown>).errorCode === 'string'
  )
}

function errorCodeOf(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined
  const data: unknown = error.response?.data
  return hasErrorCode(data) ? data.errorCode : undefined
}

export function IntakePanel({
  onSelectLead,
}: {
  onSelectLead: (leadId: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [chosenTab, setChosenTab] = React.useState<string | null>(null)
  const pollStatus = useIntakePollStatus()
  // From the always-loaded counts query; the list only loads while the panel is open.
  const counts = useDealLeadCounts()
  const issues = useIntakeIssues(open)
  const activity = useIntakeActivity(open)
  const pollNow = usePollIntakeNow()
  const retry = useRetryIntakeMessage()
  const [retrying, setRetrying] = React.useState<string | null>(null)

  const issueRows = issues.data?.data ?? []
  const stuckCount = issueRows.filter((row) => row.issue === 'STUCK').length
  const failedCount = issueRows.length - stuckCount

  const groupReason =
    stuckCount > 0 && failedCount > 0
      ? `${stuckCount} never picked up, ${failedCount} could not be read`
      : stuckCount > 0
        ? 'Never picked up by a worker'
        : failedCount > 0
          ? 'Could not be read'
          : null

  if (!pollStatus.data?.configured) return null

  // Ages from the fetch time: reading the clock in render is impure.
  const now = pollStatus.dataUpdatedAt
  const issueCount = counts.data?.processingIssues ?? 0
  const processingCount = counts.data?.processing ?? 0
  const summary = summarizeIntake(
    pollStatus.data.data,
    issueCount,
    processingCount,
    now
  )
  if (!summary) return null

  const check = () => {
    pollNow.mutate(undefined, {
      onSuccess: (result) => {
        if (result.created === 0) {
          showInfo('No new mail. The mailbox is up to date.')
          return
        }
        showSuccess(
          `${result.created} new message${result.created === 1 ? '' : 's'} captured. Processing has started.`
        )
      },
      onError: (error) => {
        const code = errorCodeOf(error)
        if (code === 'OUTLOOK_POLL_COOLDOWN') {
          showInfo('A check just ran. Try again in a moment.')
          return
        }
        if (isAxiosError(error) && !error.response) {
          showInfo('Still checking. This panel will catch up when it finishes.')
          return
        }
        showError(
          undefined,
          `The mailbox could not be checked${code ? ` (${code})` : ''}.`
        )
      },
    })
  }

  const retryOne = (id: string) => {
    setRetrying(id)
    retry.mutate(id, {
      onSuccess: () => showInfo('Queued again. It will be processed shortly.'),
      onError: (error) =>
        showError(error, 'That message could not be queued again.'),
      onSettled: () => setRetrying(null),
    })
  }

  // Sequential: each retry publishes to the same queue, so parallel only adds contention.
  const retryAll = async () => {
    setRetrying('all')
    let queued = 0
    for (const row of issueRows) {
      try {
        await retry.mutateAsync(row.id)
        queued += 1
      } catch {
        // Reported in aggregate below rather than a toast per failure.
      }
    }
    setRetrying(null)

    if (queued === issueRows.length) {
      showInfo(`${queued} queued again. They will be processed shortly.`)
    } else {
      showError(
        undefined,
        `${queued} of ${issueRows.length} queued again. The rest could not be.`
      )
    }
  }

  const tab = chosenTab ?? (issueCount > 0 ? 'issues' : 'recent')

  const warning = summary.tone === 'warning'
  const busy = summary.tone === 'busy'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Outlook intake status and recent activity"
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors',
            PILL_TONE[warning ? 'warning' : busy ? 'busy' : 'default']
          )}
        >
          {busy ? (
            <RefreshCw
              aria-hidden="true"
              className="size-3.5 shrink-0 animate-spin opacity-70"
            />
          ) : (
            <Mail aria-hidden="true" className="size-3.5 shrink-0 opacity-70" />
          )}
          <span className="font-medium">Outlook</span>
          <span aria-hidden="true" className="opacity-40">
            ·
          </span>
          {summary.label}
          <ChevronDown
            aria-hidden="true"
            className={cn('size-3 transition-transform', open && 'rotate-180')}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-120 p-0">
        <div className="flex items-start justify-between gap-3 px-3.5 pb-3 pt-3.5">
          <div className="min-w-0 space-y-1">
            <Typography
              as="p"
              variant="body-sm"
              className="truncate text-sm font-medium leading-tight"
              title={summary.mailbox}
            >
              {summary.mailbox}
            </Typography>
            <Typography
              as="p"
              variant="body-sm"
              className="text-xs leading-tight text-muted-foreground"
            >
              {summary.detail}
            </Typography>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={check}
            disabled={pollNow.isPending}
            aria-busy={pollNow.isPending}
            className={cn(PANEL_ACTION, '-mr-1.5 -mt-0.5 h-6 px-2 text-xs')}
          >
            <RefreshCw
              aria-hidden="true"
              className={cn(pollNow.isPending && 'animate-spin')}
            />
            {pollNow.isPending ? 'Checking' : 'Check'}
          </Button>
        </div>

        {processingCount > 0 && (
          <div className="flex items-center gap-2 border-t bg-muted/20 px-3 py-2">
            <RefreshCw
              aria-hidden="true"
              className="size-3 shrink-0 animate-spin text-muted-foreground"
            />
            <Typography
              as="span"
              variant="body-sm"
              className="text-xs text-muted-foreground"
            >
              {processingCount}{' '}
              {processingCount === 1 ? 'email is' : 'emails are'} being read now
            </Typography>
          </div>
        )}

        <Tabs value={tab} onValueChange={setChosenTab} className="min-h-0">
          <TabsList className="h-auto w-full justify-start gap-0.5 rounded-none border-t bg-transparent px-2 py-1.5">
            {issueCount > 0 && (
              <TabsTrigger
                value="issues"
                className="h-6 gap-1.5 px-2 text-xs text-muted-foreground data-[state=active]:border-transparent data-[state=active]:bg-muted data-[state=active]:text-foreground"
              >
                Needs attention
                <span className="text-xs tabular-nums opacity-70">
                  {issueCount}
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger
              value="recent"
              className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:border-transparent data-[state=active]:bg-muted data-[state=active]:text-foreground"
            >
              Recent
            </TabsTrigger>

            {tab === 'issues' && issueRows.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className={cn(PANEL_ACTION, 'ml-auto')}
                onClick={() => void retryAll()}
                disabled={retrying !== null}
                aria-busy={retrying === 'all'}
              >
                {retrying === 'all' ? 'Queuing' : 'Retry all'}
              </Button>
            )}
          </TabsList>

          <TabsContent value="issues" className="mt-0 max-h-80 overflow-y-auto">
            {groupReason && (
              <Typography
                as="p"
                variant="body-sm"
                className="px-3 pb-1 pt-1.5 text-xs text-muted-foreground"
              >
                {groupReason}
              </Typography>
            )}

            <ul>
              {issueRows.map((row) => {
                const stuck = row.issue === 'STUCK'
                const busy = retrying === row.id
                const mixed = stuckCount > 0 && failedCount > 0

                return (
                  <li
                    key={row.id}
                    className="group flex min-h-7 items-center gap-2 px-3 py-0.5 hover:bg-muted/50"
                  >
                    {mixed &&
                      (stuck ? (
                        <Clock
                          aria-hidden="true"
                          className="size-3 shrink-0 text-muted-foreground"
                        />
                      ) : (
                        <TriangleAlert
                          aria-hidden="true"
                          className="size-3 shrink-0 text-amber-600"
                        />
                      ))}

                    <Typography
                      as="span"
                      variant="body-sm"
                      className="min-w-0 flex-1 truncate"
                    >
                      {row.subject || '(no subject)'}
                    </Typography>

                    {!stuck && row.failedStage && (
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="shrink-0 text-xs text-muted-foreground"
                      >
                        {row.failedStage.toLowerCase()}
                      </Typography>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        PANEL_ACTION,
                        'opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100',
                        busy && 'opacity-100'
                      )}
                      onClick={() => retryOne(row.id)}
                      disabled={busy}
                      aria-busy={busy}
                      aria-label={`Retry ${row.subject || 'this message'}`}
                    >
                      {busy ? 'Queuing' : 'Retry'}
                    </Button>
                  </li>
                )
              })}
            </ul>
          </TabsContent>

          <TabsContent value="recent" className="mt-0 max-h-80 overflow-y-auto">
            {activity.isPending ? (
              <div className="space-y-2 p-3">
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
              </div>
            ) : (activity.data?.data.length ?? 0) === 0 ? (
              <Typography
                as="p"
                variant="body-sm"
                className="px-3 py-6 text-center text-xs text-muted-foreground"
              >
                Nothing yet. New mail appears here as it is read.
              </Typography>
            ) : (
              <ul>
                {activity.data?.data.map((row) => {
                  const tone = ACTIVITY_TONE[row.kind]
                  const Icon = tone.icon
                  const leadId = row.leadId

                  const body = (
                    <>
                      <Icon
                        aria-hidden="true"
                        className={cn('size-3.5 shrink-0', tone.className)}
                      />
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="min-w-0 flex-1 truncate"
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="shrink-0 text-xs tabular-nums text-muted-foreground"
                      >
                        {relativeTime(row.at, now)}
                      </Typography>
                    </>
                  )

                  return (
                    <li key={row.id}>
                      {leadId ? (
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false)
                            onSelectLead(leadId)
                          }}
                          className="flex w-full cursor-pointer items-center gap-2 px-3 py-1 text-left hover:bg-muted/50"
                        >
                          {body}
                        </button>
                      ) : (
                        <div className="flex items-start gap-2 px-3 py-2">
                          {body}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  )
}
